import {
  buildMapByNumericId,
  getFiniteNumericIdSet,
} from "../../../../utils/collections/numericId";

/**
 * @param {Array<{ worker_assignment?: Array<Object> }>} aspects
 * @param {number} numericId
 * @returns {Object|undefined}
 */
export function findWorkerObjectInAspectsByNumericId(aspects, numericId) {
  for (const aspect of aspects ?? []) {
    for (const w of aspect.worker_assignment ?? []) {
      if (Number(w?.id) === numericId) {
        return w;
      }
    }
  }
  return undefined;
}

/**
 * Compare block-level `worker_assignment` to a per-day aspect's `worker_assignment`.
 *
 * @param {{ worker_assignment?: Array<Object>|null }} block
 * @param {{ worker_assignment?: Array<Object>|null }} aspect
 * @param {boolean} [returnDifference=false] If false, return whether they differ (`boolean`). If true, return `{ removed, added }` worker objects.
 * @returns {boolean|{ removed: Array<Object>, added: Array<Object> }}
 */
export function getAssignmentDifference(block, aspect, returnDifference = false) {
  const blockAssignment = block?.worker_assignment;
  const aspectAssignment = aspect?.worker_assignment;

  const blockIds = (blockAssignment ?? [])
    .map((w) => Number(w?.id))
    .filter((n) => Number.isFinite(n));
  const aspectIds = (aspectAssignment ?? [])
    .map((w) => Number(w?.id))
    .filter((n) => Number.isFinite(n));

  const blockSig = [...blockIds].sort((a, b) => a - b).join(",");
  const aspectSig = [...aspectIds].sort((a, b) => a - b).join(",");
  const mismatch = blockSig !== aspectSig;

  if (!returnDifference) {
    return mismatch;
  }

  const blockList = Array.isArray(blockAssignment) ? blockAssignment : [];
  const aspectList = Array.isArray(aspectAssignment) ? aspectAssignment : [];
  const aspectIdSet = getFiniteNumericIdSet(aspectList);
  const blockIdSet = getFiniteNumericIdSet(blockList);

  const removed = blockList.filter((w) => {
    const id = Number(w?.id);
    return Number.isFinite(id) && !aspectIdSet.has(id);
  });
  const added = aspectList.filter((w) => {
    const id = Number(w?.id);
    return Number.isFinite(id) && !blockIdSet.has(id);
  });

  return { removed, added };
}

/**
 * Rows for override crew UI: same order as default (`block.worker_assignment`), then workers
 * only on the aspect appended in aspect list order. Each row carries a `kind` for styling.
 *
 * @param {{ worker_assignment?: Array<Object>|null }} block
 * @param {{ worker_assignment?: Array<Object>|null }} aspect
 * @returns {Array<{ worker: Object, kind: 'present'|'removed'|'added' }>}
 */
export function buildCrewOverrideDisplayRows(block, aspect) {
  const blockList = Array.isArray(block?.worker_assignment)
    ? block.worker_assignment
    : [];
  const aspectList = Array.isArray(aspect?.worker_assignment)
    ? aspect.worker_assignment
    : [];
  const aspectById = buildMapByNumericId(aspectList);
  const rows = [];
  for (const w of blockList) {
    const id = Number(w?.id);
    if (!Number.isFinite(id)) {
      continue;
    }
    if (aspectById.has(id)) {
      rows.push({ worker: aspectById.get(id), kind: "present" });
    } else {
      rows.push({ worker: w, kind: "removed" });
    }
  }
  const blockIdSet = getFiniteNumericIdSet(blockList);
  for (const w of aspectList) {
    const id = Number(w?.id);
    if (!Number.isFinite(id) || blockIdSet.has(id)) {
      continue;
    }
    rows.push({ worker: w, kind: "added" });
  }
  return rows;
}
